'use client'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'

import type { CartItemProps } from '@typings'
import { CardLazyWrapper } from '@components/cards/RaCardLazyWrapper/RaCardLazyWrapper'
import { RaPaper, RaTypography } from '@components/core/index.core'
import { useCart } from '@contexts/cart.context'
import { Loop } from '@utilities/helpers/core.helpers'

import { base64Decode } from '@/utilities/helpers/base64decode.helpers'

const fetchCartItems = async (items: unknown[]): Promise<CartItemProps[]> => {
  const res = await fetch('/api/travelplan-items/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
  if (!res.ok) return []
  const data = (await res.json()) as { cartItems?: CartItemProps[] }
  return data.cartItems ?? []
}

export const RaTravelPlanCards = () => {
  const cart = useCart()

  const params = useSearchParams()
  const travelitems = params.get('travelitems')

  const hydrateFromShareLink = async (decoded: unknown) => {
    if (!Array.isArray(decoded) || decoded.length === 0) return

    const items = await fetchCartItems(decoded)

    if (!items.length) return
    if (cart.cartItems?.length) cart.clearCart()
    cart.addToCart(items, true)
  }

  useEffect(() => {
    if (travelitems) {
      const decoded = base64Decode(travelitems)
      hydrateFromShareLink(decoded)
    }
  }, [travelitems])

  const cartItems = useMemo(() => {
    if (!cart?.cartItems || cart.cartItemsCount === 0)
      return (
        <RaTypography
          weight="semibold"
          size="l"
          align="center">
          {cart?.cartSettings?.cartIsEmptyText}
        </RaTypography>
      )

    const cardsWithParentName = cart.cartItems.map(item => ({
      ...item,
      parentTitle:
        item.type === 'additional_service'
          ? cart.cartItems.find(parent => parent.id?.toString() === item.parent)?.title
          : undefined,
    }))

    return (
      <ul>
        {Loop(cardsWithParentName, (item: CartItemProps, i: number) => (
          <CardLazyWrapper
            renderCard="travelplan"
            key={i}
            {...{ props: { item, index: i } }}
          />
        ))}
      </ul>
    )
  }, [cart?.cartItems])

  return (
    <div style={{ marginBottom: 24 }}>
      <RaPaper
        paddingMobile={4}
        padding={5}>
        {cartItems}
      </RaPaper>
    </div>
  )
}
